<template>

    <!-- Loader -->
    <app-loader :loading="loading || scanning" />

    <div class="py-2 px-4 sm:px-8">

        <h1 class="flex flex-col gap-1 pt-3 pb-8 border-b border-gray-700 mb-8 sm:flex-row sm:pt-6">
          <div class="text-2xl font-bold text-gray-200 sm:text-3xl">{{ $t('dashboard.title') }}</div>
          <select v-model="selectedSection" class="w-full ml-auto sm:w-auto">
            <option value="subcategory">{{ $t('dashboard.subcategoryChartTitle') }}</option>
            <option value="year">{{ $t('dashboard.yearChartTitle') }}</option>
            <option value="subcategoryByYear">{{ $t('dashboard.subcategoryByYearChartTitle') }}</option>
          </select>
        </h1>

        <!-- Show message when there are no expenses -->
        <div v-if="totalView <= 0" class="p-4 mb-8 bg-gray-700/20 text-white rounded-md text-center py-16">
          <p class="whitespace-pre-line">{{ noDataMessage ? $t(noDataMessage) : '' }}</p>
        </div>

        <section v-if="selectedSection === 'subcategory'">
          <h2 v-if="chartOptions.xaxis.categories.length && chartData.length" class="text-lg font-semibold text-gray-200 mb-2">
            {{ $t('dashboard.subcategoryChartTitle') }}
          </h2>
          <!-- Date range filter -->
          <form method="GET" v-if="totalView > 0" @submit.prevent>
            <div class="flex flex-col gap-3 items-stretch my-8 sm:flex-row sm:items-end">
              <div class="flex flex-row gap-3 items-center">
                <div class="flex flex-col w-full sm:w-72">
                  <label for="dashboardDateRange" class="text-gray-200 mr-2">{{ $t('filters.period') }}</label>
                  <AppDatePicker id="dashboardDateRange" v-model="dateRange" range quick-presets />
                </div>
              </div>
              <span
                v-if="totalView > 0"
                class="text-2xl font-bold text-red-500
                      px-4 py-1 h-10
                      inline-flex items-center justify-center
                      shadow-lg ring-1 ring-inset ring-gray-900
                      rounded-md bg-gray-900/70"
              >
                {{ $t('dashboard.total', { total: totalView }) }}
              </span>
            </div>
          </form>
          <apexchart
              v-if="chartOptions.xaxis.categories.length && chartData.length"
              ref="dashboardChart"
              :key="chartKey"
              :width="screenWidth <= 768 ? '100%' : '100%'"
              :height="chartHeight"
              type="bar"
              :options="chartOptions"
              :series="chartData"
              :auto-update-series="false"
              :auto-update-options="false"
          ></apexchart>
        </section>

        <!-- <hr class="my-8 border-gray-700" /> -->

        <section v-if="selectedSection === 'subcategoryByYear'">
          <h2 v-if="subcatByYearOptions.xaxis.categories.length && subcatByYearData.length" class="text-lg font-semibold text-gray-200 mb-2">
              {{ $t('dashboard.subcategoryByYearChartTitle') }}
          </h2>
          <apexchart
              v-if="subcatByYearOptions.xaxis.categories.length && subcatByYearData.length"
              ref="subcatByYearChart"
              :key="subcatByYearKey"
              width="100%"
              :height="chartHeight"
              type="bar"
              :options="subcatByYearOptions"
              :series="subcatByYearData"
              :auto-update-series="false"
              :auto-update-options="false"
          ></apexchart>
        </section>

        <!-- <hr class="my-8 border-gray-700" /> -->

        <section v-if="selectedSection === 'year'">
          <h2 v-if="yearChartOptions.xaxis.categories.length && yearChartData.length" class="text-lg font-semibold text-gray-200 mb-2">
              {{ $t('dashboard.yearChartTitle') }}
          </h2>
          <div v-if="yearChartOptions.xaxis.categories.length && yearChartData.length" :class="yearChartScrollable ? 'overflow-x-auto' : ''">
              <apexchart
                  ref="yearChart"
                  :key="yearChartKey"
                  :width="yearChartWidth"
                  height="350px"
                  type="bar"
                  :options="yearChartOptions"
                  :series="yearChartData"
                  :auto-update-series="false"
                  :auto-update-options="false"
              ></apexchart>
          </div>
        </section>

        <!-- Add some extra space here sofab button doesn't cover edit/delete button when now pagination is added -->
        <div class="h-16"></div>

        <app-fab-menu
            @add="openAddModal"
            @scanned="onReceiptScanned"
            @update:scanning="scanning = $event"
        />

        <!-- Add Expense Modal -->
        <add-modal
            ref="addModal"
            :is-add-modal-open="isAddModalOpen"
            :prefill="receiptPrefill"
            @update:is-add-modal-open="isAddModalOpen = $event"
            @expense-actions="handleExpenseActions"
        ></add-modal>

    </div>

</template>

<script>
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
// import { useToast } from 'vue-toastification';
import apiClient from '@/api/apiClient'; 
import { useTheme } from '@/composables/useTheme';
import { useOnLocaleChange } from '@/composables/useOnLocaleChange';
import { t } from '@/i18n';
import VueApexCharts from "vue3-apexcharts";
import AppLoader from "@/components/common/AppLoader.vue";
import AppFabMenu from "@/components/common/AppFabMenu.vue";
import AppDatePicker from "@/components/common/AppDatePicker.vue";
import AddModal from "@/components/expenses/AddModal";

export default {
    components: {
        apexchart: VueApexCharts,
        AppLoader,
        AppFabMenu,
        AppDatePicker,
        AddModal
    },
    setup() {
        // const toast = useToast();
        const DATE_RANGE_STORAGE_KEY = 'dashboard-date-range';

        const toIsoDate = (date) => date.toISOString().slice(0, 10);

        const defaultDateRange = () => {
            const now = new Date();
            const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
            return { startDate: toIsoDate(firstOfMonth), endDate: toIsoDate(now) };
        };

        const loadStoredDateRange = () => {
            try {
                const stored = JSON.parse(localStorage.getItem(DATE_RANGE_STORAGE_KEY));
                if (stored && stored.startDate && stored.endDate) {
                    return stored;
                }
            } catch (error) {
                console.error('Error reading stored date range:', error);
            }
            return defaultDateRange();
        };

        const initialDateRange = loadStoredDateRange();

        const selectedSection = ref('subcategory');
        const totalView = ref(0);
        const currMonth = ref(new Date().toLocaleString('en-US', { month: 'long' }));
        const screenWidth = ref(window.innerWidth);
        const isAddModalOpen = ref(false);
        const scanning = ref(false);
        // Blank by default; replaced with the scan's best-effort reading right before the modal
        // opens (a *new* object, so FormModal's values watcher refills it)
        const receiptPrefill = ref({});
        const chartData = ref([]);
        const yearChartData = ref([]);
        const yearChart = ref(null);
        const yearChartKey = ref(0); // changing it re-creates the chart
        const subcatByYearData = ref([]);
        const subcatByYearChart = ref(null);
        const subcatByYearKey = ref(0); // changing it re-creates the chart
        const subcategories = ref([]);
        const startDate = ref(initialDateRange.startDate);
        const endDate = ref(initialDateRange.endDate);
        const noDataMessage = ref('');
        const loading = ref(false);
        const expenseCountArray = ref([]); // Separate array to store expense_count
        const dashboardChart = ref(null);
        const chartKey = ref(0); // changing it re-creates the chart
        const addModal = ref(null);
        const { isDark } = useTheme();
        // Chart text can't use CSS classes, so it needs the color for the current theme
        const chartTextColor = () => (isDark.value ? '#fff' : '#111827');
        // Bars are distributed, so ApexCharts takes one data label color per bar
        const dataLabelColors = () => Array(100).fill(chartTextColor());
        const chartOptions = ref({
            chart: {
                type: 'bar',
                id: 'subcat-bar',
                toolbar: {
                    show: false,
                },
                // We recreate the chart ourselves on real width changes (see updateScreenWidth);
                // ApexCharts' own resize listener also fires on mobile address-bar show/hide
                // (height-only changes) and redraws/flickers the chart, so disable it.
                redrawOnWindowResize: false,
                redrawOnParentResize: false,
            },
            xaxis: {
                categories: [], // Array to store category names (subcategory names)
                labels: {
                    style: {
                        colors: chartTextColor(),
                        fontSize: '10px',
                    },
                },
            },
            yaxis: {
                labels: {
                    style: {
                        colors: chartTextColor(),
                        fontSize: '10px',
                    },
                },
            },
            plotOptions: {
                bar: {
                    horizontal: true,
                    barHeight: '60%',
                    distributed: true, // This will remove the empty space between bars
                },
            },
            dataLabels: {
                enabled: true,
                style: {
                    colors: dataLabelColors(),
                },
                formatter: function (val) {
                    // Check if the value is an exact number (has no decimal part)
                    if (Number.isInteger(val)) {
                        return val.toFixed(2); // Add .00 for exact numbers
                    }
                    return val; // For non-exact numbers, show the original value with two decimal places
                },
            },
            tooltip: {
                enabled: true,
                custom: function({ series, seriesIndex, dataPointIndex }) {
                    const expenseCount = expenseCountArray.value[seriesIndex][dataPointIndex]; // Get the expense_count from the separate array
                    return `<div class="py-4 px-8 bg-gray-900 border-0 ring-0">
                                <div class="text-white"><b>${t('dashboard.tooltipValue')}</b> $${series[seriesIndex][dataPointIndex]}</div>
                                <div class="text-white"><b>${t('dashboard.tooltipEntries')}</b> ${expenseCount}</div>
                            </div>`;
                }.bind(this), // Bind the custom function to the Vue instance
            },
            grid: {
                row: {
                    colors: ['transparent'],
                    opacity: 0,
                },
            },
            stroke: {
                show: true,
                width: 1,
                colors: ['transparent'],
            },
            legend: {
                fontSize: '14px',
                fontFamily: 'Helvetica, Arial',
                fontWeight: 400,
                markers: {
                    width: 12,
                    height: 12,
                },
                itemMargin: {
                    horizontal: 12,
                    vertical: 0,
                },
                labels: {
                    colors: chartTextColor(),
                },
            },
        });

        const yearChartOptions = ref({
            chart: {
                type: 'bar',
                id: 'year-bar',
                toolbar: {
                    show: false,
                },
                redrawOnWindowResize: false,
                redrawOnParentResize: false,
            },
            xaxis: {
                categories: [], // Array to store year labels
                labels: {
                    style: {
                        colors: chartTextColor(),
                        fontSize: '12px',
                    },
                },
            },
            yaxis: {
                labels: {
                    style: {
                        colors: chartTextColor(),
                        fontSize: '10px',
                    },
                },
            },
            plotOptions: {
                bar: {
                    horizontal: false,
                    columnWidth: '45%',
                    distributed: true,
                },
            },
            dataLabels: {
                enabled: true,
                style: {
                    colors: dataLabelColors(),
                },
                formatter: function (val) {
                    return val.toFixed(2);
                },
            },
            tooltip: {
                enabled: false,
            },
            legend: {
                show: false,
            },
            grid: {
                row: {
                    colors: ['transparent'],
                    opacity: 0,
                },
            },
            stroke: {
                show: true,
                width: 1,
                colors: ['transparent'],
            },
        });

        const subcatByYearOptions = ref({
            chart: {
                type: 'bar',
                id: 'subcat-by-year-bar',
                toolbar: {
                    show: false,
                },
                redrawOnWindowResize: false,
                redrawOnParentResize: false,
            },
            xaxis: {
                categories: [], // Array to store category names (subcategory names)
                labels: {
                    style: {
                        colors: chartTextColor(),
                        fontSize: '10px',
                    },
                },
            },
            yaxis: {
                labels: {
                    style: {
                        colors: chartTextColor(),
                        fontSize: '10px',
                    },
                },
            },
            plotOptions: {
                bar: {
                    horizontal: true,
                    barHeight: '80%',
                },
            },
            dataLabels: {
                enabled: false,
            },
            tooltip: {
                enabled: true,
                custom: function({ series, seriesIndex, dataPointIndex, w }) {
                    const year = w.config.series[seriesIndex].name;
                    const value = series[seriesIndex][dataPointIndex];
                    return `<div class="py-4 px-8 bg-gray-900 border-0 ring-0">
                                <div class="text-white"><b>${year}:</b> $${value.toFixed(2)}</div>
                            </div>`;
                },
            },
            grid: {
                row: {
                    colors: ['transparent'],
                    opacity: 0,
                },
            },
            stroke: {
                show: true,
                width: 1,
                colors: ['transparent'],
            },
            legend: {
                fontSize: '14px',
                fontFamily: 'Helvetica, Arial',
                fontWeight: 400,
                markers: {
                    width: 12,
                    height: 12,
                },
                itemMargin: {
                    horizontal: 12,
                    vertical: 0,
                },
                labels: {
                    colors: chartTextColor(),
                },
            },
        });

        // ApexCharts reshapes some options once rendered (e.g. yaxis becomes an array), so update defensively
        const applyThemeColors = (options) => {
            const color = chartTextColor();
            [options.xaxis, ...[].concat(options.yaxis || [])].forEach((axis) => {
                if (axis && axis.labels && axis.labels.style) {
                    axis.labels.style.colors = color;
                }
            });
            if (options.legend && options.legend.labels) {
                options.legend.labels.colors = color;
            }
            if (options.dataLabels && options.dataLabels.style) {
                options.dataLabels.style.colors = dataLabelColors();
            }
        };

        watch(isDark, () => {
            applyThemeColors(chartOptions.value);
            applyThemeColors(yearChartOptions.value);
            applyThemeColors(subcatByYearOptions.value);

            // The chart doesn't notice in-place changes to its options, so push them explicitly
            if (dashboardChart.value && typeof dashboardChart.value.updateOptions === 'function') {
                dashboardChart.value.updateOptions(chartOptions.value);
            }
            if (yearChart.value && typeof yearChart.value.updateOptions === 'function') {
                yearChart.value.updateOptions(yearChartOptions.value);
            }
            if (subcatByYearChart.value && typeof subcatByYearChart.value.updateOptions === 'function') {
                subcatByYearChart.value.updateOptions(subcatByYearOptions.value);
            }
        });

        const chartHeight = computed(() => {
            return screenWidth.value <= 768 ? "400px" : "600px";
        });

        // Keep each year's column at a comfortable minimum width so the chart scrolls
        // horizontally instead of squeezing bars together as more years are added
        const minPxPerYear = 120;

        const yearChartScrollable = computed(() => {
            const yearCount = yearChartOptions.value.xaxis.categories.length;
            return (yearCount * minPxPerYear) > screenWidth.value;
        });

        const yearChartWidth = computed(() => {
            const yearCount = yearChartOptions.value.xaxis.categories.length;
            return yearChartScrollable.value ? `${yearCount * minPxPerYear}px` : '100%';
        });

        // Methods ------------------------------------------------------
        const fetchSubcategories = () => {
            apiClient.get('/get-subcategories.php')
            .then((response) => {
                // Set the subcategories data to the response from the backend
                subcategories.value = response.data;
            })
            .catch((error) => {
                console.error('Error fetching subcategories:', error);
            });
        };

        const fillBarChart = async () => {
            // console.log('fillBarChart() called');
            loading.value = true;

            try {
                const response = await apiClient.post('/get-dashboard-subcategories.php', {
                    startDate: startDate.value,
                    endDate: endDate.value,
                }, {
                    responseType: 'json'
                });

                // console.log('API Response:', response.data);

                const totalExpenses = response.data.map(item => item.total_expenses);
                const expenseCount = response.data.map(item => item.expense_count);
                const subcategoryNames = response.data.map(item => item.subcategory_name);

                // console.log('Total Expenses:', totalExpenses);
                // console.log('Expense Count:', expenseCount);
                // console.log('Subcategory Names:', subcategoryNames);

                if (totalExpenses.length && subcategoryNames.length) {
                    chartData.value = [
                        {
                            name: 'Total Expenses',
                            data: totalExpenses,
                        }
                    ];

                    expenseCountArray.value = [expenseCount];
                    chartOptions.value.xaxis.categories = subcategoryNames;

                    // Re-create the chart for every new dataset. Updating series and options in place
                    // raced inside ApexCharts and threw "Cannot read properties of undefined (reading 'indexOf')"
                    chartKey.value++;

                } else {
                    noDataMessage.value = 'dashboard.noData'; // a translation key, so it follows the language
                }

            } catch (error) {
                console.error('Error fetching data or updating chart:', error);
            } finally {
                loading.value = false;
            }
        };

        const fillYearChart = async () => {
            try {
                const response = await apiClient.post('/get-dashboard-years.php');

                const years = response.data.map(item => item.year);
                const totals = response.data.map(item => item.total_expenses);

                if (years.length) {
                    yearChartData.value = [
                        {
                            name: 'Total Expenses',
                            data: totals,
                        }
                    ];
                    yearChartOptions.value.xaxis.categories = years;
                    yearChartKey.value++;
                }
            } catch (error) {
                console.error('Error fetching year data or updating chart:', error);
            }
        };

        const fillSubcategoryByYearChart = async () => {
            try {
                const response = await apiClient.post('/get-dashboard-subcategories-by-year.php');

                if (!response.data.length) {
                    return;
                }

                // Pivot the flat (subcategory, year, total) rows into one series per year,
                // each with one value per subcategory (0 when that pair has no expenses)
                const subcategoryNames = [...new Set(response.data.map(item => item.subcategory_name))];
                const years = [...new Set(response.data.map(item => item.year))].sort();

                const totalsByYearAndSubcategory = {};
                response.data.forEach(item => {
                    totalsByYearAndSubcategory[`${item.year}|${item.subcategory_name}`] = item.total_expenses;
                });

                subcatByYearData.value = years.map(year => ({
                    name: String(year),
                    data: subcategoryNames.map(name => Number(totalsByYearAndSubcategory[`${year}|${name}`] || 0)),
                }));
                subcatByYearOptions.value.xaxis.categories = subcategoryNames;
                subcatByYearKey.value++;
            } catch (error) {
                console.error('Error fetching subcategory-by-year data or updating chart:', error);
            }
        };

        const fillTotalExpense = async () => {
            try {
                const response = await apiClient.post('/get-dashboard-total.php', {
                    startDate: startDate.value,
                    endDate: endDate.value,
                });

                if (response.data > 0) {
                    totalView.value = response.data;
                } else {
                    noDataMessage.value = 'dashboard.noData'; // a translation key, so it follows the language
                }

            } catch (error) {
                console.error(error);
            }
        };

        const openAddModal = () => {
            fetchSubcategories();
            receiptPrefill.value = {};
            isAddModalOpen.value = true;
        };

        const onReceiptScanned = (prefill) => {
            fetchSubcategories();
            receiptPrefill.value = prefill;
            isAddModalOpen.value = true;
        };

        const handleExpenseActions = () => {
            fillBarChart();
            fillTotalExpense();
            fillYearChart();
            fillSubcategoryByYearChart();
        };

        const updateDateRange = () => {
            try {
                localStorage.setItem(DATE_RANGE_STORAGE_KEY, JSON.stringify({
                    startDate: startDate.value,
                    endDate: endDate.value,
                }));
            } catch (error) {
                console.error('Error storing date range:', error);
            }
            fillBarChart();
            fillTotalExpense();
        };

        // The picker works with [start, end]; the rest of the page keeps the two refs
        const dateRange = computed({
            get: () => [startDate.value, endDate.value],
            set: ([start, end]) => {
                startDate.value = start;
                endDate.value = end;
                updateDateRange();
            },
        });

        let resizeTimeout = null;

        const updateScreenWidth = () => {
            const newWidth = window.innerWidth;
            // On mobile, scrolling shows/hides the address bar, which fires resize
            // events for a height change only (width stays the same). Ignore those so
            // the charts don't get recreated (and flicker) on every scroll.
            if (newWidth === screenWidth.value) {
                return;
            }
            screenWidth.value = newWidth;

            // ApexCharts corrupts itself when its width/height props change on a live
            // instance (same issue as the dataset-update race noted above), so force a
            // full recreation once the resize settles instead of live-resizing it.
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                chartKey.value++;
                yearChartKey.value++;
                subcatByYearKey.value++;
            }, 200);
        };

        onMounted(() => {
            fillBarChart();
            fillTotalExpense();
            fillYearChart();
            fillSubcategoryByYearChart();
            window.addEventListener('resize', updateScreenWidth);
        });

        onUnmounted(() => {
            window.removeEventListener('resize', updateScreenWidth);
            clearTimeout(resizeTimeout);
        });

        // the names of the subcategories in the chart come from the API in the current language
        useOnLocaleChange(fillBarChart);
        useOnLocaleChange(fillSubcategoryByYearChart);

        return {
            selectedSection,
            totalView,
            currMonth,
            screenWidth,
            isAddModalOpen,
            scanning,
            receiptPrefill,
            chartData,
            yearChartData,
            yearChart,
            yearChartKey,
            yearChartOptions,
            subcatByYearData,
            subcatByYearChart,
            subcatByYearKey,
            subcatByYearOptions,
            subcategories,
            startDate,
            endDate,
            noDataMessage,
            loading,
            chartOptions,
            chartHeight,
            yearChartWidth,
            yearChartScrollable,
            fillBarChart,
            fillYearChart,
            fillSubcategoryByYearChart,
            fillTotalExpense,
            openAddModal,
            onReceiptScanned,
            handleExpenseActions,
            dateRange,
            dashboardChart,
            chartKey,
            addModal
        };
    }
}
</script>
  
<style>
/* Add any custom styles specific to ExpensesView here */
</style>
